import React, { useCallback, useEffect, useState } from 'react'
import { DatePicker, Divider, Form, Radio, Row } from 'antd'
import Swal from 'sweetalert2'
import ContentHeader from '../../components/shared/ContentHeader'
import StatisticsGraph from '../../components/statistics/StatisticsGraph'
import statisticsApi from '../../api/statistics.api'
import { useRecoilState } from 'recoil'
import { userState } from '../../store/user'
import dayjs from 'dayjs'
import CustomLoading from '../../components/shared/CustomLoading'
import styles from '../../styles/statistics.module.scss'

const dateTypeItems = [
  { label: '1주일', value: '1week' },
  { label: '1개월', value: '1month' },
  { label: '3개월', value: '3month' },
]

const InflowStatisticsPage = () => {
  const [user, setUser] = useRecoilState(userState)
  const [isLoading, setIsLoading] = useState(false)

  const [navType, setNavType] = useState('mall')
  const [optionList, setOptionList] = useState([])

  const [dateType, setDateType] = useState('1week')
  const [searchOptions, setSearchOptions] = useState({
    searchType: 'order_created_at',
    searchStatus: 'gift_address_ready',
    searchDetailType: '',
    searchDetail: '',
    searchOrderId: '',
    funnel: 1, // 유입경로, 1:전체
    name: '',
    phone: '',
    startDate: dayjs().subtract(7, 'days').startOf('day'),
    endDate: dayjs().endOf('day'),
  })

  const [summary, setSummary] = useState({})
  const [graphData, setGraphData] = useState({})

  /**
   * 1주일, 1개월, 3개월
   * @param e
   */
  const handleDateTypeChange = (e) => {
    const value = e.target.value
    setDateType(value)
    switch (value) {
      case '1week':
        if (searchOptions.endDate === null || searchOptions.endDate === '') {
          setSearchOptions({
            ...searchOptions,
            startDate: dayjs().subtract(7, 'days').startOf('day'),
            endDate: dayjs().endOf('day'),
          })
        } else {
          setSearchOptions({
            ...searchOptions,
            startDate: dayjs(searchOptions.endDate.format('YYYY/MM/DD'))
              .subtract(7, 'days')
              .startOf('day'),
          })
        }
        break
      case '1month':
        if (searchOptions.endDate === null || searchOptions.endDate === '') {
          setSearchOptions({
            ...searchOptions,
            startDate: dayjs().subtract(1, 'months').startOf('day'),
            endDate: dayjs().endOf('day'),
          })
        } else {
          setSearchOptions({
            ...searchOptions,
            startDate: dayjs(searchOptions.endDate.format('YYYY/MM/DD'))
              .subtract(1, 'months')
              .startOf('day'),
          })
        }
        break
      case '3month':
        if (searchOptions.endDate === null || searchOptions.endDate === '') {
          setSearchOptions({
            ...searchOptions,
            startDate: dayjs().subtract(3, 'months').startOf('day'),
            endDate: dayjs().endOf('day'),
          })
        } else {
          setSearchOptions({
            ...searchOptions,
            startDate: dayjs(searchOptions.endDate.format('YYYY/MM/DD'))
              .subtract(3, 'months')
              .startOf('day'),
          })
        }
        break
    }
  }

  /**
   * 초기화
   */
  const handleInitial = () => {
    setSearchOptions({
      searchType: 'order_created_at',
      searchStatus: 'gift_address_ready',
      searchOrderId: '',
      funnel: 1,
      name: '',
      phone: '',
      startDate: dayjs().subtract(7, 'days').startOf('day'),
      endDate: dayjs().endOf('day'),
    })
    setDateType('1week')
  }

  /**
   * 최대 30일 조회 기간 설정
   * @param value
   * @param type
   */
  const handleDatePicker = (value, type) => {
    setDateType('')
    if (type === 'startDate') {
      let diffDays = dayjs(searchOptions.endDate).diff(value, 'days')
      if (diffDays > 0) {
        if (diffDays >= 30) {
          // alert("조회 기간은 30일 이내로 설정 가능합니다.");
          Swal.fire({
            title: '조회 기간은 30일 이내로 설정 가능합니다.',
            customClass: 'custom-alert',
            confirmButtonColor: '#117ffa',
            confirmButtonText: '확인',
          })
          handleInitial()
        } else {
          setSearchOptions({ ...searchOptions, [type]: value })
        }
      } else {
        Swal.fire({
          title: '시작 날짜와 마지막 날짜를 정확히 선택해주세요.',
          customClass: 'custom-alert',
          confirmButtonColor: '#117ffa',
          confirmButtonText: '확인',
        })
        handleInitial()
      }
    } else {
      let diffDays = dayjs(value).diff(searchOptions.startDate, 'days')
      if (diffDays > 0) {
        if (diffDays >= 30) {
          Swal.fire({
            title: '조회 기간은 30일 이내로 설정 가능합니다.',
            customClass: 'custom-alert',
            confirmButtonColor: '#117ffa',
            confirmButtonText: '확인',
          })
          handleInitial()
        } else {
          setSearchOptions({ ...searchOptions, [type]: value })
        }
      } else {
        Swal.fire({
          title: '시작 날짜와 마지막 날짜를 정확히 선택해주세요.',
          customClass: 'custom-alert',
          confirmButtonColor: '#117ffa',
          confirmButtonText: '확인',
        })
        handleInitial()
      }
    }
  }

  useEffect(() => {
    getInflowDataGraph() // user, dateType, searchOptions, navType
  }, [user, dateType, searchOptions, navType])

  /**
   * 매출 그래프 데이터 조회
   * @returns {Promise<void>}
   */
  const getInflowDataGraph = useCallback(async () => {
    setIsLoading(true)
    // 당영몰: 0, 드럭스토어 정보 가져올때 인플루언서 전체: 1, 나머지는 인플루언서 id
    let partners_id = navType === 'mall' ? 0 : searchOptions.funnel

    const param = {
      searchDateType: dateType?.length > 0 ? dateType : 'custom',
      startDate: dayjs(searchOptions.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(searchOptions.endDate).format('YYYY-MM-DD'),
      partnersId: partners_id,
    }
    // const res = await statisticsApi.readInflowDataGraph(param, user.id)
    // if (res.data.code === 200) {
    //   setSummary(res.data.result.totalSummary)
    //   // total_sales_amount, total_purchases_num, unit_price_avg
    //   setGraphData(res.data.result.userStatistics)
    // }
    setIsLoading(false)
  }, [user, dateType, searchOptions, navType])

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && <CustomLoading />}
      <ContentHeader title="유입분석" />
      <div className={`${styles['product-register-wrap']}`}>
        <div className={`${styles['product-search-box-wrap']}`}>
          <Form.Item
            label="조회기간"
            labelAlign="left"
            colon={false}
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 21 }}
            style={{ marginBottom: 0 }}
          >
            <Row align={'middle'} style={{ gap: '5px', rowGap: '5px' }}>
              <Radio.Group
                className={`${styles['options-list']}`}
                options={dateTypeItems}
                onChange={handleDateTypeChange}
                value={dateType}
                optionType="button"
              />
              <DatePicker
                className={`${styles['order-search-date-wrap']}`}
                format="YYYY/MM/DD"
                value={searchOptions.startDate}
                onChange={(value) => handleDatePicker(value, 'startDate')}
                allowClear={false}
              />
              ~
              <DatePicker
                className={`${styles['order-search-date-wrap']}`}
                format="YYYY/MM/DD"
                value={searchOptions.endDate}
                onChange={(value) => handleDatePicker(value, 'endDate')}
                allowClear={false}
              />
            </Row>
          </Form.Item>
        </div>
        <StatisticsGraph
          title={'유입분석'}
          searchOptions={searchOptions}
          summary={summary}
          graphData={graphData}
        />
      </div>
    </div>
  )
}
export default InflowStatisticsPage
